import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Collapse,
  IconButton,
  Avatar,
  TextField,
  LinearProgress,
  Dialog,
} from '@mui/material';
import {
  CloudDownload,
  Google,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Delete,
  LocationOn,
  Star,
  CalendarToday,
  Restaurant,
  Payment,
  AutoAwesome,
  TravelExplore,
  PlayArrow,
  Stop,
  Email,
  Pause,
  Replay,
} from '@mui/icons-material';
import firebase from 'firebase/app';
import TabDescription from './TabDescription';
import 'firebase/firestore';
import 'firebase/auth';
import axios from 'axios';
import AdminMenuCVImport from './AdminMenuCVImport';
import { useRegionContext } from '../../region/RegionContext';

const TOTAL_CITIES = 1719; // matches ALL_SEARCH_POINTS.length in venueImport.ts

const PAGE_SIZE = 25;

const ImportedVenues = () => {
  const { db, endpoints } = useRegionContext();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [stateFilter, setStateFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(0);
  const [sendingStripe, setSendingStripe] = useState(null);
  const [scrapingMenuId, setScrapingMenuId] = useState(null);
  const [cvVenue, setCvVenue] = useState(null); // venue open in CV import dialog
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // ── Email scraper state (live from Firestore) ────────────────────────────────
  const [scrapeJob, setScrapeJob] = useState(null);
  const [scrapeControlling, setScrapeControlling] = useState(false);

  const SCRAPE_ESTIMATED_TOTAL = 33000;

  useEffect(() => {
    const unsubscribe = db.doc('_scrapeJobs/emailScrape').onSnapshot(snap => {
      setScrapeJob(snap.exists ? snap.data() : null);
    });
    return () => unsubscribe();
  }, []);

  const handleScrapeControl = async (action) => {
    setScrapeControlling(true);
    try {
      const idToken = await firebase.auth().currentUser.getIdToken();
      await axios.post(
        endpoints.controlEmailScrape,
        { action },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
    } catch (err) {
      alert(err?.response?.data?.error || err.message || 'Control action failed');
    } finally {
      setScrapeControlling(false);
    }
  };

  // 'default' is valid for Chip but NOT for LinearProgress — return 'primary' as fallback
  const scrapeStatusColor = (status) => {
    if (status === 'running' || status === 'processing') return 'success';
    if (status === 'paused') return 'warning';
    if (status === 'complete') return 'info';
    return 'primary';
  };

  // Chip supports 'default'; use it when the job hasn't started
  const scrapeChipColor = (status) => {
    if (status === 'running' || status === 'processing') return 'success';
    if (status === 'paused') return 'warning';
    if (status === 'complete') return 'info';
    return 'default';
  };

  const scrapeStatusLabel = (status) => {
    if (status === 'running') return 'Running';
    if (status === 'processing') return 'Processing batch…';
    if (status === 'paused') return 'Paused';
    if (status === 'complete') return 'Complete';
    return 'Not started';
  };

  const formatEta = (etaHours) => {
    if (etaHours === null || etaHours === undefined) return 'Calculating…';
    if (etaHours < 0.1) return '< 5 min';
    if (etaHours < 1) return `~${Math.round(etaHours * 60)} min`;
    return `~${etaHours}h`;
  };

  // ── Bulk import state ───────────────────────────────────────────────────────
  const [importRunning, setImportRunning] = useState(false);
  const [importBatchSize, setImportBatchSize] = useState(5);
  const [importStartIndex, setImportStartIndex] = useState(0);
  const [importMaxCities, setImportMaxCities] = useState(TOTAL_CITIES);
  const [importProgress, setImportProgress] = useState(null);
  const importStopRef = useRef(false);

  const handleBulkImport = async () => {
    if (importRunning) return;
    importStopRef.current = false;
    setImportRunning(true);
    setImportProgress({
      currentIndex: importStartIndex,
      totalCreated: 0,
      totalSkipped: 0,
      totalErrors: 0,
      log: [],
      done: false,
    });

    let startCityIndex = importStartIndex;
    const maxCities = importMaxCities;

    try {
      while (startCityIndex < maxCities && !importStopRef.current) {
        // Get a fresh ID token for each batch (auto-refreshes if near expiry)
        const idToken = await firebase.auth().currentUser.getIdToken();
        const count = Math.min(importBatchSize, maxCities - startCityIndex);

        let respData;
        try {
          const resp = await axios.post(
            endpoints.bulkImportVenues,
            { startCityIndex, count },
            { headers: { Authorization: `Bearer ${idToken}` } },
          );
          respData = resp.data;
        } catch (err) {
          const msg = err?.response?.data?.message || err.message || 'Unknown error';
          setImportProgress(prev => ({
            ...prev,
            log: [`✗ Error at city ${startCityIndex}: ${msg}`, ...(prev.log || []).slice(0, 49)],
          }));
          break;
        }

        const { nextStartIndex, hasMore, results } = respData;
        const cityBreakdown = Object.entries(results.cities || {})
          .map(([c, n]) => `${c}: ${n}`)
          .join(', ');

        setImportProgress(prev => ({
          ...prev,
          currentIndex: nextStartIndex,
          totalCreated: prev.totalCreated + (results.created || 0),
          totalSkipped: prev.totalSkipped + (results.skipped || 0),
          totalErrors: prev.totalErrors + (results.errors || 0),
          log: [
            `Cities ${startCityIndex}–${nextStartIndex - 1}: +${results.created} new, ${results.skipped} skipped${cityBreakdown ? ` (${cityBreakdown})` : ''}`,
            ...(prev.log || []).slice(0, 49),
          ],
        }));

        if (!hasMore) {
          setImportProgress(prev => ({ ...prev, done: true }));
          break;
        }
        startCityIndex = nextStartIndex;

        // Brief pause between batches to avoid hammering the function
        await new Promise(r => setTimeout(r, 500));
      }
    } finally {
      setImportRunning(false);
    }
  };

  const handleStopImport = () => {
    importStopRef.current = true;
  };

  useEffect(() => {
    loadImportedVenues();
  }, [dateFilter, sourceFilter, stateFilter]);

  const loadImportedVenues = async () => {
    try {
      setLoading(true);

      // Build query
      let query = db.collection('Business');

      // Filter by source
      if (sourceFilter === 'google') {
        query = query.where('sourceGoogle', '==', true);
      } else if (sourceFilter === 'manual') {
        query = query.where('sourceGoogle', '==', false);
      }

      // Filter by state
      if (stateFilter !== 'all') {
        query = query.where('importState', '==', stateFilter);
      }

      // Get all venues
      const snapshot = await query.orderBy('importedAt', 'desc').limit(500).get();

      let data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      // Apply date filter (client-side since we need timestamp conversion)
      if (dateFilter !== 'all') {
        const now = new Date();
        const cutoffDate = new Date();

        if (dateFilter === '7days') {
          cutoffDate.setDate(now.getDate() - 7);
        } else if (dateFilter === '30days') {
          cutoffDate.setDate(now.getDate() - 30);
        } else if (dateFilter === '90days') {
          cutoffDate.setDate(now.getDate() - 90);
        }

        data = data.filter(venue => {
          if (!venue.importedAt) return false;
          const importDate = venue.importedAt.toDate();
          return importDate >= cutoffDate;
        });
      }

      setVenues(data);
      setPage(0);
    } catch (err) {
      console.error('Error loading imported venues:', err);
      setError('Failed to load imported venues');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyVenue = async (venueId) => {
    try {
      await db.collection('Business').doc(venueId).update({
        bizVerified: true,
        verifiedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
      alert('Venue verified successfully!');
      loadImportedVenues();
    } catch (err) {
      console.error('Error verifying venue:', err);
      alert('Failed to verify venue');
    }
  };

  const handleScrapeMenu = async (venue) => {
    if (!venue.bizWeb) {
      alert('No website URL for this venue. Add one in the edit page first.');
      return;
    }
    if (!window.confirm(`Scrape menu from ${venue.bizWeb}?\nThis may take up to 2 minutes.`)) return;
    try {
      setScrapingMenuId(venue.id);
      const resp = await axios.post(
        endpoints.scrapeVenueMenu,
        { businessId: venue.id, websiteUrl: venue.bizWeb }
      );
      const d = resp.data;
      const msg = d.pdfFound
        ? 'PDF menu found and saved to Storage.'
        : d.menuImagesFound
        ? `${d.menuImagesFound} menu image(s) saved to Storage.`
        : d.itemsFound
        ? `${d.itemsFound} menu items extracted and saved.`
        : d.message || 'Scrape complete — no menu found.';
      alert(msg);
      loadImportedVenues();
    } catch (err) {
      alert(err?.response?.data?.error || 'Scrape failed. Check that the website is publicly accessible.');
    } finally {
      setScrapingMenuId(null);
    }
  };

  const handleSendStripeOnboarding = async (venue) => {
    if (!window.confirm(`Send Stripe onboarding link to ${venue.bizName}?`)) {
      return;
    }

    try {
      setSendingStripe(venue.id);

      await axios.post(
        endpoints.sendStripeOnboarding,
        {
          bizId: venue.id,
          bizName: venue.bizName,
          bizEmail: venue.bizEmail || venue.userEmail,
        }
      );

      alert(`Stripe onboarding link sent to ${venue.bizName}.`);
    } catch (err) {
      console.error('Failed to send Stripe onboarding:', err);
      alert('Failed to send Stripe onboarding link. Please try again.');
    } finally {
      setSendingStripe(null);
    }
  };

  const handleDeleteVenue = async (venueId, venueName) => {
    if (!window.confirm(`Delete ${venueName}? This cannot be undone.`)) {
      return;
    }

    try {
      await db.collection('Business').doc(venueId).delete();
      alert('Venue deleted successfully!');
      loadImportedVenues();
    } catch (err) {
      console.error('Error deleting venue:', err);
      alert('Failed to delete venue');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate();
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getUniqueStates = () => {
    const states = [...new Set(venues.map(v => v.importState || v.bizState).filter(Boolean))];
    return states.sort();
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading imported venues...</Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const googleVenues = venues.filter(v => v.sourceGoogle).length;
  const manualVenues = venues.filter(v => !v.sourceGoogle).length;
  const verifiedCount = venues.filter(v => v.bizVerified).length;

  return (
    <Box>
      <TabDescription>
        Venues bulk-imported from Google Places and other external sources, stored in the Business collection
        with an importedAt timestamp. Use the import panel to pull new cities from Google Places, run email
        scraping to find contact details, and trigger CV menu extraction for venues with websites. Review
        imported venues for accuracy before verifying them to appear on the platform.
      </TabDescription>
      {/* ── Bulk Import Panel ─────────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Google sx={{ color: 'info.main' }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Bulk Import from Google Places
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Start City Index"
                type="number"
                size="small"
                fullWidth
                value={importStartIndex}
                onChange={e => setImportStartIndex(Math.max(0, parseInt(e.target.value, 10) || 0))}
                disabled={importRunning}
                inputProps={{ min: 0, max: TOTAL_CITIES - 1 }}
                helperText={`0–${TOTAL_CITIES - 1}`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Batch Size (cities)"
                type="number"
                size="small"
                fullWidth
                value={importBatchSize}
                onChange={e => setImportBatchSize(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 5)))}
                disabled={importRunning}
                inputProps={{ min: 1, max: 20 }}
                helperText="Cities per API call (1–20)"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                label="Max Cities"
                type="number"
                size="small"
                fullWidth
                value={importMaxCities}
                onChange={e => setImportMaxCities(Math.max(1, parseInt(e.target.value, 10) || TOTAL_CITIES))}
                disabled={importRunning}
                inputProps={{ min: 1 }}
                helperText={`Total cities: ${TOTAL_CITIES}`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={importRunning ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                  onClick={handleBulkImport}
                  disabled={importRunning}
                  fullWidth
                >
                  {importRunning ? 'Importing…' : 'Start Import'}
                </Button>
                {importRunning && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Stop />}
                    onClick={handleStopImport}
                  >
                    Stop
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          {importProgress && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, (importProgress.currentIndex / importMaxCities) * 100)}
                  sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                />
                <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                  {importProgress.currentIndex} / {importMaxCities} cities
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Chip label={`+${importProgress.totalCreated} imported`} color="success" size="small" />
                <Chip label={`${importProgress.totalSkipped} skipped (duplicates)`} size="small" />
                {importProgress.totalErrors > 0 && (
                  <Chip label={`${importProgress.totalErrors} errors`} color="error" size="small" />
                )}
                {importProgress.done && (
                  <Chip label="Complete" color="success" variant="outlined" size="small" />
                )}
                {!importRunning && !importProgress.done && importProgress.currentIndex > 0 && (
                  <Chip
                    label={`Stopped at city ${importProgress.currentIndex} — click to resume`}
                    color="warning"
                    size="small"
                    onClick={() => setImportStartIndex(importProgress.currentIndex)}
                    sx={{ cursor: 'pointer' }}
                  />
                )}
              </Box>
              {importProgress.log.length > 0 && (
                <Box
                  sx={{
                    bgcolor: 'grey.900',
                    color: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    p: 1.5,
                    borderRadius: 1,
                    maxHeight: 180,
                    overflowY: 'auto',
                  }}
                >
                  {importProgress.log.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Email Scraper Panel ────────────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email sx={{ color: 'secondary.main' }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Website Email Scraper
              </Typography>
            </Box>
            <Chip
              label={scrapeStatusLabel(scrapeJob?.status)}
              color={scrapeChipColor(scrapeJob?.status)}
              size="small"
              variant={scrapeJob?.status === 'running' || scrapeJob?.status === 'processing' ? 'filled' : 'outlined'}
            />
          </Box>

          {/* Progress bar */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {scrapeJob
                  ? `${(scrapeJob.totalSeen ?? 0).toLocaleString()} / ~${SCRAPE_ESTIMATED_TOTAL.toLocaleString()} venues`
                  : 'Not yet started'}
              </Typography>
              {scrapeJob && (
                <Typography variant="caption" color="text.secondary">
                  {Math.min(100, Math.round(((scrapeJob.totalSeen ?? 0) / SCRAPE_ESTIMATED_TOTAL) * 100))}%
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant={scrapeJob?.status === 'processing' ? 'indeterminate' : 'determinate'}
              value={scrapeJob
                ? Math.min(100, ((scrapeJob.totalSeen ?? 0) / SCRAPE_ESTIMATED_TOTAL) * 100)
                : 0}
              color={scrapeStatusColor(scrapeJob?.status)}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {/* Stats chips */}
          {scrapeJob && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip
                icon={<Email sx={{ fontSize: 14 }} />}
                label={`${(scrapeJob.totalFound ?? 0).toLocaleString()} emails found`}
                color="success"
                size="small"
              />
              <Chip
                label={`${scrapeJob.hitRate ?? 0}% hit rate`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${(scrapeJob.totalNotFound ?? 0).toLocaleString()} no email on page`}
                size="small"
                variant="outlined"
              />
              <Chip
                label={`${(scrapeJob.totalNoWebsite ?? 0).toLocaleString()} no website`}
                size="small"
                variant="outlined"
              />
              {scrapeJob.status !== 'complete' && (
                <Chip
                  label={`ETA: ${formatEta(scrapeJob.etaHours)}`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
              {scrapeJob.status === 'complete' && (
                <Chip label="All venues processed" color="info" size="small" />
              )}
            </Box>
          )}

          {/* Control buttons */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {/* Start — shown when no job exists, or job is complete */}
            {(!scrapeJob || scrapeJob.status === 'complete') && (
              <Button
                variant="contained"
                color="secondary"
                startIcon={scrapeControlling ? <CircularProgress size={16} color="inherit" /> : <PlayArrow />}
                onClick={() => handleScrapeControl('start')}
                disabled={scrapeControlling}
                size="small"
              >
                {scrapeJob?.status === 'complete' ? 'Re-run Scraper' : 'Start Scraper'}
              </Button>
            )}

            {/* Pause — shown when running or processing */}
            {(scrapeJob?.status === 'running' || scrapeJob?.status === 'processing') && (
              <Button
                variant="outlined"
                color="warning"
                startIcon={scrapeControlling ? <CircularProgress size={16} color="inherit" /> : <Pause />}
                onClick={() => handleScrapeControl('pause')}
                disabled={scrapeControlling}
                size="small"
              >
                Pause
              </Button>
            )}

            {/* Resume — shown when paused */}
            {scrapeJob?.status === 'paused' && (
              <Button
                variant="contained"
                color="success"
                startIcon={scrapeControlling ? <CircularProgress size={16} color="inherit" /> : <Replay />}
                onClick={() => handleScrapeControl('resume')}
                disabled={scrapeControlling}
                size="small"
              >
                Resume
              </Button>
            )}
          </Box>

          {scrapeJob && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              The scraper runs autonomously on GCP every 5 minutes — pausing here stops the next batch without interrupting the current one.
              {scrapeJob.updatedAt && ` Last updated: ${new Date(scrapeJob.updatedAt.toDate()).toLocaleTimeString('en-AU')}.`}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Restaurant sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Total Imported
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {venues.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Google sx={{ fontSize: 40, color: 'info.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    From Google
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {googleVenues}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CloudDownload sx={{ fontSize: 40, color: 'secondary.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Manual Entries
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {manualVenues}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                <Box>
                  <Typography color="text.secondary" variant="body2">
                    Verified
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    {verifiedCount}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Import Date</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="Import Date"
                >
                  <MenuItem value="all">All Time</MenuItem>
                  <MenuItem value="7days">Last 7 Days</MenuItem>
                  <MenuItem value="30days">Last 30 Days</MenuItem>
                  <MenuItem value="90days">Last 90 Days</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  label="Source"
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  <MenuItem value="google">Google Places</MenuItem>
                  <MenuItem value="manual">Manual Entry</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  label="State"
                >
                  <MenuItem value="all">All States</MenuItem>
                  {getUniqueStates().map(state => (
                    <MenuItem key={state} value={state}>{state}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Venues Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            Imported Venues ({venues.length})
          </Typography>

          {venues.length === 0 ? (
            <Alert severity="info">No venues found matching the filters</Alert>
          ) : (
            <>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button variant="outlined" size="small" disabled={page === 0} onClick={() => { setPage(p => p - 1); setExpandedId(null); }}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page + 1} of {Math.ceil(venues.length / PAGE_SIZE)} ({venues.length} total)
              </Typography>
              <Button variant="outlined" size="small" disabled={(page + 1) * PAGE_SIZE >= venues.length} onClick={() => { setPage(p => p + 1); setExpandedId(null); }}>
                Next
              </Button>
            </Box>
            <TableContainer component={Paper} sx={{ mt: 2 }}>
              <Table aria-label="Imported venues">
                <TableHead>
                  <TableRow>
                    <TableCell scope="col" />
                    <TableCell scope="col"><strong>Venue Name</strong></TableCell>
                    <TableCell scope="col"><strong>Location</strong></TableCell>
                    <TableCell scope="col"><strong>Source</strong></TableCell>
                    <TableCell scope="col"><strong>Imported</strong></TableCell>
                    <TableCell scope="col"><strong>Rating</strong></TableCell>
                    <TableCell scope="col"><strong>Status</strong></TableCell>
                    <TableCell scope="col"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {venues.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((venue) => (
                    <React.Fragment key={venue.id}>
                      <TableRow
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => setExpandedId(expandedId === venue.id ? null : venue.id)}
                      >
                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label={expandedId === venue.id ? 'Collapse venue details' : 'Expand venue details'}
                            aria-expanded={expandedId === venue.id}
                            aria-controls={`venue-details-${venue.id}`}
                          >
                            {expandedId === venue.id ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {venue.bizPic1 ? (
                              <Avatar src={venue.bizPic1} alt={venue.bizName} />
                            ) : (
                              <Avatar><Restaurant /></Avatar>
                            )}
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {venue.bizName || 'Unnamed'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {venue.importCity || venue.bizSuburb || 'Unknown'}, {venue.importState || venue.bizState || ''}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {venue.sourceGoogle ? (
                            <Chip
                              icon={<Google />}
                              label="Google"
                              size="small"
                              color="info"
                            />
                          ) : (
                            <Chip label="Manual" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(venue.importedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star sx={{ fontSize: 16, color: 'gold' }} />
                            <Typography variant="body2">
                              {venue.googleRating || venue.bizRating || 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {venue.bizVerified ? (
                            <Chip label="Verified" color="success" size="small" />
                          ) : (
                            <Chip label="Unverified" color="default" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            {!venue.bizVerified && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerifyVenue(venue.id);
                                }}
                              >
                                Verify
                              </Button>
                            )}
                            {venue.bizWeb && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={scrapingMenuId === venue.id
                                  ? <CircularProgress size={12} />
                                  : <TravelExplore />}
                                disabled={!!scrapingMenuId}
                                onClick={(e) => { e.stopPropagation(); handleScrapeMenu(venue); }}
                                sx={{ whiteSpace: 'nowrap' }}
                              >
                                {scrapingMenuId === venue.id ? 'Scraping…' : 'Scrape Menu'}
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              color="secondary"
                              startIcon={<AutoAwesome sx={{ fontSize: 14 }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setCvVenue(venue);
                              }}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              Scan Menu
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={sendingStripe === venue.id ? <CircularProgress size={12} /> : <Payment />}
                              disabled={sendingStripe === venue.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSendStripeOnboarding(venue);
                              }}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {sendingStripe === venue.id ? 'Sending...' : 'Stripe'}
                            </Button>
                            <IconButton
                              size="medium"
                              color="error"
                              aria-label="Delete venue"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteVenue(venue.id, venue.bizName);
                              }}
                              sx={{ p: 1.5 }}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {/* Expanded Details */}
                      <TableRow>
                        <TableCell colSpan={8} sx={{ py: 0 }}>
                          <Collapse in={expandedId === venue.id} timeout="auto" unmountOnExit id={`venue-details-${venue.id}`}>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                    Full Address
                                  </Typography>
                                  <Typography variant="body2" sx={{ pl: 3 }}>
                                    {venue.bizAddress || 'Not available'}
                                  </Typography>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                  <Typography variant="subtitle2" gutterBottom>
                                    Contact Information
                                  </Typography>
                                  <Typography variant="body2" sx={{ pl: 3 }}>
                                    <strong>Phone:</strong> {venue.bizPhone || 'N/A'}<br />
                                    <strong>Website:</strong> {venue.bizWeb ? (
                                      <a href={venue.bizWeb} target="_blank" rel="noopener noreferrer">
                                        {venue.bizWeb}
                                      </a>
                                    ) : 'N/A'}
                                  </Typography>
                                </Grid>

                                {venue.googlePlaceId && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Google Information
                                    </Typography>
                                    <Typography variant="body2" sx={{ pl: 3 }}>
                                      <strong>Place ID:</strong> {venue.googlePlaceId}<br />
                                      <strong>Reviews:</strong> {venue.googleReviewCount || 0} reviews<br />
                                      <strong>Wheelchair Accessible:</strong> {venue.googleWheelchairAccessible ? 'Yes' : 'Unknown'}
                                    </Typography>
                                  </Grid>
                                )}

                                {venue.bizEditorialSummary && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Editorial Summary
                                    </Typography>
                                    <Typography variant="body2" sx={{ pl: 3, fontStyle: 'italic' }}>
                                      {venue.bizEditorialSummary}
                                    </Typography>
                                  </Grid>
                                )}

                                {/* Service Options */}
                                {(venue.bizDineIn != null || venue.bizDelivery != null || venue.bizTakeout != null || venue.bizCurbsidePickup != null) && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Service Options
                                    </Typography>
                                    <Box sx={{ pl: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {[
                                        { label: 'Dine-in', value: venue.bizDineIn },
                                        { label: 'Delivery', value: venue.bizDelivery },
                                        { label: 'Takeout', value: venue.bizTakeout },
                                        { label: 'Curbside pickup', value: venue.bizCurbsidePickup },
                                      ].filter(o => o.value != null).map(o => (
                                        <Chip key={o.label} label={o.label} size="small" color={o.value ? 'success' : 'default'} variant={o.value ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {/* Meals & Drinks */}
                                {(venue.bizServesBreakfast != null || venue.bizServesLunch != null || venue.bizServesDinner != null || venue.bizServesBeer != null || venue.bizServesWine != null || venue.bizServesCoffee != null) && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Meals & Drinks
                                    </Typography>
                                    <Box sx={{ pl: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {[
                                        { label: 'Breakfast', value: venue.bizServesBreakfast },
                                        { label: 'Brunch', value: venue.bizServesBrunch },
                                        { label: 'Lunch', value: venue.bizServesLunch },
                                        { label: 'Dinner', value: venue.bizServesDinner },
                                        { label: 'Dessert', value: venue.bizServesDessert },
                                        { label: 'Vegetarian', value: venue.bizServesVegetarianFood },
                                        { label: 'Coffee', value: venue.bizServesCoffee },
                                        { label: 'Beer', value: venue.bizServesBeer },
                                        { label: 'Wine', value: venue.bizServesWine },
                                        { label: 'Cocktails', value: venue.bizServesCocktails },
                                      ].filter(o => o.value != null).map(o => (
                                        <Chip key={o.label} label={o.label} size="small" color={o.value ? 'success' : 'default'} variant={o.value ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {/* Amenities */}
                                {(venue.bizOutdoorSeating != null || venue.bizLiveMusic != null || venue.bizAllowsDogs != null || venue.bizGoodForGroups != null || venue.bizGoodForChildren != null || venue.bizRestroom != null) && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Amenities
                                    </Typography>
                                    <Box sx={{ pl: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {[
                                        { label: 'Outdoor seating', value: venue.bizOutdoorSeating },
                                        { label: 'Live music', value: venue.bizLiveMusic },
                                        { label: "Children's menu", value: venue.bizMenuForChildren },
                                        { label: 'Good for children', value: venue.bizGoodForChildren },
                                        { label: 'Good for groups', value: venue.bizGoodForGroups },
                                        { label: 'Watching sports', value: venue.bizGoodForWatchingSports },
                                        { label: 'Dogs allowed', value: venue.bizAllowsDogs },
                                        { label: 'Restroom', value: venue.bizRestroom },
                                      ].filter(o => o.value != null).map(o => (
                                        <Chip key={o.label} label={o.label} size="small" color={o.value ? 'success' : 'default'} variant={o.value ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {/* Accessibility */}
                                {venue.bizAccessibility && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Accessibility (Google)
                                    </Typography>
                                    <Box sx={{ pl: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {[
                                        { label: 'Wheelchair entrance', value: venue.bizAccessibility?.wheelchairAccessibleEntrance },
                                        { label: 'Wheelchair parking', value: venue.bizAccessibility?.wheelchairAccessibleParking },
                                        { label: 'Wheelchair restroom', value: venue.bizAccessibility?.wheelchairAccessibleRestroom },
                                        { label: 'Wheelchair seating', value: venue.bizAccessibility?.wheelchairAccessibleSeating },
                                      ].filter(o => o.value != null).map(o => (
                                        <Chip key={o.label} label={o.label} size="small" color={o.value ? 'success' : 'default'} variant={o.value ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {/* Parking */}
                                {venue.bizParking && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Parking
                                    </Typography>
                                    <Box sx={{ pl: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {[
                                        { label: 'Free lot', value: venue.bizParking?.freeParkingLot },
                                        { label: 'Paid lot', value: venue.bizParking?.paidParkingLot },
                                        { label: 'Free street', value: venue.bizParking?.freeStreetParking },
                                        { label: 'Paid street', value: venue.bizParking?.paidStreetParking },
                                        { label: 'Valet', value: venue.bizParking?.valetParking },
                                        { label: 'Free garage', value: venue.bizParking?.freeGarageParking },
                                        { label: 'Paid garage', value: venue.bizParking?.paidGarageParking },
                                      ].filter(o => o.value != null).map(o => (
                                        <Chip key={o.label} label={o.label} size="small" color={o.value ? 'success' : 'default'} variant={o.value ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {/* Payment Options */}
                                {venue.bizPaymentOptions && (
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Payment Options
                                    </Typography>
                                    <Box sx={{ pl: 3, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                      {[
                                        { label: 'Credit cards', value: venue.bizPaymentOptions?.acceptsCreditCards },
                                        { label: 'Debit cards', value: venue.bizPaymentOptions?.acceptsDebitCards },
                                        { label: 'Cash only', value: venue.bizPaymentOptions?.acceptsCashOnly },
                                        { label: 'Contactless (NFC)', value: venue.bizPaymentOptions?.acceptsNfc },
                                      ].filter(o => o.value != null).map(o => (
                                        <Chip key={o.label} label={o.label} size="small" color={o.value ? 'success' : 'default'} variant={o.value ? 'filled' : 'outlined'} sx={{ mr: 0.5, mb: 0.5 }} />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}

                                {venue.images && venue.images.length > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Images
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pl: 3 }}>
                                      {venue.images.slice(0, 5).map((img, idx) => (
                                        <img
                                          key={idx}
                                          src={img}
                                          alt={`Venue ${idx + 1}`}
                                          style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }}
                                          onClick={(e) => { e.stopPropagation(); setLightboxSrc(img); }}
                                        />
                                      ))}
                                    </Box>
                                  </Grid>
                                )}
                              </Grid>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      {/* Image lightbox */}
      <Dialog
        open={!!lightboxSrc}
        onClose={() => setLightboxSrc(null)}
        maxWidth="lg"
      >
        {lightboxSrc && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setLightboxSrc(null)}
              aria-label="Close lightbox"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                zIndex: 1,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1 }}>X</Typography>
            </IconButton>
            <Box
              component="img"
              src={lightboxSrc}
              alt="Full size"
              sx={{ maxWidth: '90vw', maxHeight: '90vh', display: 'block' }}
            />
          </Box>
        )}
      </Dialog>

      {/* CV import dialog — opened by "Scan Menu" button on any venue row */}
      {cvVenue && (
        <AdminMenuCVImport
          venue={cvVenue}
          open={Boolean(cvVenue)}
          onClose={() => setCvVenue(null)}
          onImported={() => {
            setCvVenue(null);
            loadImportedVenues();
          }}
        />
      )}
    </Box>
  );
};

export default ImportedVenues;
